// Kill attribution + execute eligibility. ADR 0014 §2.6 / §2.7 / 軸 9 §6.1-3。
//
// 召喚体 kill の所有者帰属:
//   - 召喚体 (Entity.isPet === true) が kill した時、SummonInstance.ownerId で
//     owner を逆引きし、owner を kill 主体として扱う (`onKill` reactive event /
//     `kill_streak` 加算)。
//   - 軸 4 (`onSummonDied`) は被攻撃側で発火、軸 9 (`onKill` / `kill_streak`) は
//     owner 側で発火 → 機構分離で同時発火禁止 (ADR 0014 §2.6)。
//
// execute 即死不可対象:
//   - `bossFlag` / `eliteFlag` true の敵は軸 9 archetype 4 (Finisher) で
//     即死不可。catalogue 投入 (ADR 0015) で archetype 4 の reducer から
//     `canExecute(target)` を参照。
//
// kill_streak (max 7 stack):
//   - `onKillEvent` で `kill_streak` stack +1 (caps at 7)
//   - `onTakenDamageReset` で stack 0 化 + `lastTakenDamageTick` 更新
//   - 戦闘終了 = `state.entities` クリアで自然消滅 (durationTicks 設定なし)
import { PERMANENT_BUFF_EXPIRES_AT } from '@renavora/shared';
/** Maximum kill_streak stack count. 軸 9 §6.1 で pin 済。 */
export const KILL_STREAK_MAX_STACKS = 7;
/**
 * Resolve the kill owner for an attacker. For pet (`isPet === true`) attackers,
 * walks `state.summonByEntity` to find the SummonInstance and returns its
 * `ownerId`. Non-pet attackers return their own id.
 *
 * Fallback (pet 但し summon record 未登録) は attacker.id を返す (safe default、
 * 通常は spawnSummons で必ず登録されるため到達しない)。
 */
export function resolveKillOwner(attacker, state) {
    if (!attacker.isPet)
        return attacker.id;
    const inst = state.summonByEntity.get(attacker.id);
    if (inst === undefined)
        return attacker.id;
    return inst.ownerId;
}
/**
 * Determines whether a target may be instantly killed by `execute` (軸 9
 * archetype 4 Finisher) effects. Returns `false` for bosses / elites
 * (per 軸 9 §6.3 — 種族名ベース判定は使用しない).
 */
export function canExecute(target) {
    return target.bossFlag !== true && target.eliteFlag !== true;
}
/**
 * Fire kill-attribution side effects for a kill event:
 *   1. `kill_streak` buff stack +1 (caps at 7)
 *   2. (Optional, ADR 0015) reactive `onKill` skill fire
 *
 * 本 PR では `kill_streak` 増分のみ実装し、reactive Skill 発火は
 * `reactive-events.ts` に委譲する (caller 側で `fireReactiveSkills` を呼ぶ
 * 想定。Step 8 / Step 9 で接続)。
 *
 * Same-tick multi-kill:
 *   - 同 tick 内で複数 kill → stack は kill 回数分加算 (max 7 でクランプ、軸 9 §6.1)
 */
export function onKillEvent(killerId, _victim, state) {
    const killer = state.entities.find((e) => e.id === killerId);
    if (killer === undefined || killer.hp <= 0)
        return;
    const existing = killer.buffs.find((b) => b.id === 'kill_streak');
    if (existing !== undefined) {
        existing.stacks = Math.min(KILL_STREAK_MAX_STACKS, existing.stacks + 1);
    }
    else {
        const buff = {
            id: 'kill_streak',
            stacks: 1,
            // 戦闘中持続: durationTicks なしのため、maintainBuffs が drop しない
            // sentinel (battle 終了でクリア)。UI はこの値を検出して `∞` 表示にする。
            expiresAt: PERMANENT_BUFF_EXPIRES_AT,
        };
        killer.buffs.push(buff);
    }
}
/**
 * Reset kill_streak and update lastTakenDamageTick when an entity takes
 * damage. Called from the damage hook in `applyDamageWithHooks` (Step 9).
 *
 * `kill_streak` reset:
 *   - `onTakenDamage` event 発火対象すべてで stack 0 化 (軸 9 §6.1 E1 採用)
 *   - DoT tick / shield 受けでも reset (Q-SS SUGG、軸 9 §6.1 「被弾の定義」)
 *
 * `state.lastTakenDamageTick[entityId] = state.tick`:
 *   - archetype 9 (No-Death Bonus) 判定で参照 (catalogue 投入時、ADR 0015)
 */
export function onTakenDamageReset(entityId, state) {
    const entity = state.entities.find((e) => e.id === entityId);
    if (entity === undefined)
        return;
    const idx = entity.buffs.findIndex((b) => b.id === 'kill_streak');
    if (idx >= 0)
        entity.buffs.splice(idx, 1);
    state.lastTakenDamageTick.set(entityId, state.tick);
}
//# sourceMappingURL=kill-attribution.js.map